import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Share2, Accessibility } from "lucide-react";
import VenueMap from "@/components/VenueMap";
import { CATEGORIES } from "@/data/constants";

export default function PublicTrip() {
  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/trips/public/${slug}`).then(r => setTrip(r.data)).catch(() => setError("Trip not found or is private"));
  }, [slug]);

  if (error) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-lg text-gray-700">{error}</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/">Back to Access Local</Link></Button>
    </div>
  );
  if (!trip) return <div className="min-h-[40vh] flex items-center justify-center text-gray-500">Loading…</div>;

  const catName = (slug) => CATEGORIES.find(c => c.slug === slug)?.short || slug;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-2">
        <Badge className="rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200"><Share2 className="w-3 h-3 mr-1"/>Shared trip</Badge>
        <span className="text-xs text-gray-500">by {trip.user_name}</span>
      </div>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-gray-900">{trip.name}</h1>
      {trip.description && <p className="mt-3 text-lg text-gray-700 max-w-2xl">{trip.description}</p>}

      <ol className="mt-8 space-y-3">
        {(trip.venues || []).map((v, i) => (
          <li key={v.id} className="bg-white border border-gray-200 rounded-xl p-5 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
              <div>
                <Link to={`/venue/${v.id}`} className="font-heading font-semibold text-xl text-gray-900 hover:underline">{v.venue_name}</Link>
                <div className="text-sm text-gray-600 flex items-center gap-1"><MapPin className="w-4 h-4"/>{catName(v.category)} · {v.area}{v.postcode ? ` · ${v.postcode}` : ""}</div>
                <p className="mt-2 text-sm text-gray-700 line-clamp-2">{v.description}</p>
                {(v.features || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {v.features.slice(0, 3).map(f => <Badge key={f} variant="outline" className="rounded-full border-gray-300">{f}</Badge>)}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {(trip.venues || []).some(v => typeof v.lat === "number") && (
        <div className="mt-10">
          <h2 className="font-heading text-2xl font-semibold mb-3">On the map</h2>
          <VenueMap venues={trip.venues} height={380} />
        </div>
      )}

      <Card className="mt-12 border-2 border-gray-900 rounded-xl">
        <CardContent className="p-6 flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center"><Accessibility className="w-6 h-6"/></div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-semibold">Build your own trip on Access Local</h3>
            <p className="text-sm text-gray-700">Save accessible places, group them into trips, and share your favourites.</p>
          </div>
          <Button asChild className="rounded-full bg-blue-600 hover:bg-blue-700"><Link to="/register">Join free</Link></Button>
        </CardContent>
      </Card>
    </div>
  );
}
